import { ArslActionApplyDroppedColumns, ArsListingState, EMPTY_HEADER_COL_ID } from "../interface";
import { datasetsFromColumns } from "./datasetsFromColumns";

/**
 * Inserts or replaces listing columns based on a header drop action.
 *
 * @param state - Current ARS listing state
 * @param action - Dropped-columns action describing target placement
 * @returns Updated ARS listing state, capped to 20 columns
 */
export const arsl_applyDroppedColumns = <T>(
  state: ArsListingState<T>,
  action: ArslActionApplyDroppedColumns<T>,
): ArsListingState<T> => {
  if (state.bodyData.arsListingColumns.length >= 20) {
    return state;
  }

  const existingColumns = state.bodyData.arsListingColumns;

  if (existingColumns.length === 0 || action.colId === EMPTY_HEADER_COL_ID) {
    return {
      ...state,
      changed: true,
      bodyData: {
        ...state.bodyData,
        arsListingColumns: action.columns.slice(0, 20),
      },
    };
  }

  const targetIndex = existingColumns.findIndex((column) => column.id === action.colId);
  let updatedColumns: typeof existingColumns;

  if (targetIndex < 0) {
    updatedColumns = [...existingColumns, ...action.columns];
  } else if (action.dropAction === "insert-before") {
    updatedColumns = [
      ...existingColumns.slice(0, targetIndex),
      ...action.columns,
      ...existingColumns.slice(targetIndex),
    ];
  } else if (action.dropAction === "insert-after") {
    updatedColumns = [
      ...existingColumns.slice(0, targetIndex + 1),
      ...action.columns,
      ...existingColumns.slice(targetIndex + 1),
    ];
  } else {
    const [replacementColumn, ...remainingColumns] = action.columns;
    const nextColumns = [...existingColumns];
    nextColumns[targetIndex] = replacementColumn;
    if (remainingColumns.length > 0) {
      nextColumns.splice(targetIndex + 1, 0, ...remainingColumns);
    }
    updatedColumns = nextColumns;
  }

  const newState: ArsListingState<T> = {
    ...state,
    bodyData: {
      ...state.bodyData,
      arsListingColumns: updatedColumns.slice(0, 20),
    },
    changed: true,
    datasets: datasetsFromColumns(updatedColumns),
  };

  return newState;
};
